import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { FirstAidKit, Phone, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SOSButton() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [sosData, setSosData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSOSClick = () => {
    setIsConfirmOpen(true);
  };

  const confirmSOS = async () => {
    setIsLoading(true);
    setIsConfirmOpen(false);
    
    try {
      const { data } = await axios.post(
        `${API_URL}/api/sos`,
        { symptoms: '', location: '' },
        { withCredentials: true }
      );
      setSosData(data);
      setIsDialogOpen(true);
    } catch (error) {
      console.error('SOS error:', error);
      // Still show emergency dialog even on error
      setSosData({
        message: 'SOS activat! Apelați imediat 112!',
        emergency_number: '112',
        patient_info: {}
      });
      setIsDialogOpen(true);
    }
    setIsLoading(false);
  };

  return (
    <>
      {/* SOS Button - Fixed position */}
      <button
        onClick={handleSOSClick}
        disabled={isLoading}
        className="fixed bottom-6 right-24 z-[9999] w-16 h-16 bg-destructive hover:bg-red-600 text-white rounded-full shadow-xl flex items-center justify-center sos-button-pulse transition-transform hover:scale-110"
        data-testid="sos-button"
        aria-label="Emergency SOS"
      >
        <FirstAidKit weight="fill" className="w-8 h-8" />
      </button>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="sos-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Warning weight="fill" className="w-6 h-6" />
              Confirmare SOS
            </DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să activezi protocolul de urgență? 
              Aceasta va pregăti informațiile tale medicale pentru serviciile de urgență.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button 
              variant="outline" 
              onClick={() => setIsConfirmOpen(false)}
              data-testid="sos-cancel-button"
            >
              Anulează
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmSOS}
              className="gap-2"
              data-testid="sos-confirm-button"
            >
              <Phone weight="fill" className="w-4 h-4" />
              Activează SOS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Info Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="sos-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive text-xl">
              <FirstAidKit weight="fill" className="w-7 h-7" />
              URGENȚĂ ACTIVATĂ
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Emergency Number */}
            <div className="bg-destructive/10 border-2 border-destructive rounded-lg p-6 text-center">
              <p className="text-sm text-destructive mb-2">APELAȚI IMEDIAT</p>
              <a 
                href="tel:112" 
                className="text-5xl font-bold text-destructive hover:underline"
                data-testid="emergency-call-link"
              >
                112
              </a>
            </div>

            {/* Patient Info */}
            {sosData?.patient_info && (
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-foreground">Informații pentru serviciile de urgență:</h4>
                
                {sosData.patient_info.name && (
                  <div>
                    <span className="text-sm text-muted-foreground">Pacient: </span>
                    <span className="font-medium">{sosData.patient_info.name}</span>
                  </div>
                )}
                
                {sosData.patient_info.cui && (
                  <div>
                    <span className="text-sm text-muted-foreground">CUI: </span>
                    <span className="font-medium">{sosData.patient_info.cui}</span>
                  </div>
                )}

                {sosData.patient_info.allergies?.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Alergii: </span>
                    <span className="font-medium text-destructive">
                      {sosData.patient_info.allergies.join(', ')}
                    </span>
                  </div>
                )}

                {sosData.patient_info.chronic_illnesses?.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Boli cronice: </span>
                    <span className="font-medium">
                      {sosData.patient_info.chronic_illnesses.join(', ')}
                    </span>
                  </div>
                )}

                {sosData.patient_info.medications?.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Medicație: </span>
                    <span className="font-medium">
                      {sosData.patient_info.medications.join(', ')}
                    </span>
                  </div>
                )}
              </div>
            )}

            <p className="text-sm text-muted-foreground text-center">
              Rămâneți calm și așteptați echipa de urgență.
            </p>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDialogOpen(false)}
              className="w-full"
              data-testid="sos-close-button"
            >
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
